import React, { useRef, useState } from 'react';
import { Camera, Upload, X, Loader2, Images, Send } from 'lucide-react';
import { compressImage } from '../utils/imageCompression';

interface BatchUploadProps {
    onImagesSelected: (images: string[], mimeType: string) => void;
    isLoading: boolean;
    maxImages?: number;
}

interface QueuedImage {
    id: string;
    base64: string;
    thumbnail: string;
    originalName: string;
}

const BatchUpload: React.FC<BatchUploadProps> = ({
    onImagesSelected,
    isLoading,
    maxImages = 10
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);
    const [queue, setQueue] = useState<QueuedImage[]>([]);
    const [processing, setProcessing] = useState(false);

    const handleFiles = async (files: FileList | null) => {
        if (!files || files.length === 0) return;

        setProcessing(true);
        const newImages: QueuedImage[] = [];

        for (let i = 0; i < files.length && queue.length + newImages.length < maxImages; i++) {
            const file = files[i];
            if (!file.type.startsWith('image/')) continue;

            try {
                // Compress for API (max 1024px, quality reduction loop)
                const { base64 } = await compressImage(file, { maxWidthOrHeight: 1024, quality: 0.7 });

                // Create small thumbnail for preview (200px)
                const { base64: thumbnail } = await compressImage(file, { maxWidthOrHeight: 200, quality: 0.5 });

                newImages.push({
                    id: `img_${Date.now()}_${i}`,
                    base64,
                    thumbnail,
                    originalName: file.name
                });
            } catch (e) {
                console.error('Failed to process', file.name, e);
            }
        }

        setQueue(prev => [...prev, ...newImages]);
        setProcessing(false);

        // Reset file input
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (cameraInputRef.current) cameraInputRef.current.value = '';
    };

    const removeImage = (id: string) => {
        setQueue(prev => prev.filter(img => img.id !== id));
    };

    const clearAll = () => {
        setQueue([]);
    };

    const submitBatch = () => {
        if (queue.length === 0) return;
        const images = queue.map(img => img.base64);
        onImagesSelected(images, 'image/jpeg');
    };

    if (isLoading) {
        return (
            <div className="w-full max-w-xl mx-auto px-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-blue-100 flex flex-col items-center">
                    <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
                    <p className="text-slate-600">Processing {queue.length} cards...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-2xl mx-auto px-4">
            <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-indigo-100">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Images className="w-5 h-5 text-indigo-600" />
                        <h3 className="text-lg font-bold text-slate-800">Batch Scan</h3>
                    </div>
                    <span className="text-sm text-slate-400">{queue.length}/{maxImages} cards</span>
                </div>

                {/* Image Queue Preview */}
                {queue.length > 0 && (
                    <div className="mb-4">
                        <div className="flex flex-wrap gap-2">
                            {queue.map((img) => (
                                <div key={img.id} className="relative w-16 h-20 rounded-lg overflow-hidden border border-slate-200 group">
                                    <img
                                        src={`data:image/jpeg;base64,${img.thumbnail}`}
                                        alt={img.originalName}
                                        className="w-full h-full object-cover"
                                    />
                                    <button
                                        onClick={() => removeImage(img.id)}
                                        className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button
                            onClick={clearAll}
                            className="text-xs text-slate-400 hover:text-red-500 mt-2"
                        >
                            Clear All
                        </button>
                    </div>
                )}

                {/* Upload Buttons */}
                <div className="flex gap-3 mb-4">
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={processing || queue.length >= maxImages}
                        className="flex-1 flex items-center justify-center gap-2 bg-white border-2 border-indigo-200 hover:border-indigo-400 text-indigo-700 py-3 rounded-xl font-medium transition-colors disabled:opacity-50"
                    >
                        <Upload className="w-5 h-5" />
                        {processing ? 'Processing...' : 'Select Files'}
                    </button>
                    <button
                        onClick={() => cameraInputRef.current?.click()}
                        disabled={processing || queue.length >= maxImages}
                        className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-xl font-medium transition-colors disabled:opacity-50"
                    >
                        <Camera className="w-5 h-5" />
                        Camera
                    </button>
                </div>

                {/* Submit Button */}
                {queue.length > 0 && (
                    <button
                        onClick={submitBatch}
                        disabled={processing}
                        className="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-xl font-bold text-lg shadow-lg shadow-green-200 transition-all flex items-center justify-center gap-2"
                    >
                        <Send className="w-5 h-5" />
                        Analyze {queue.length} Card{queue.length > 1 ? 's' : ''}
                    </button>
                )}

                {/* Help Text */}
                {queue.length === 0 && (
                    <p className="text-center text-sm text-slate-400 mt-2">
                        Add up to {maxImages} cards, then analyze all at once
                    </p>
                )}

                {/* Hidden Inputs */}
                <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleFiles(e.target.files)}
                />
                <input
                    type="file"
                    ref={cameraInputRef}
                    className="hidden"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => handleFiles(e.target.files)}
                />
            </div>
        </div>
    );
};

export default BatchUpload;
